import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { apiFetch } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { withUnidade } from '../../utils/storageByUnit';

const CAMPO_VAZIO = () => ({
  key: `doc_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
  nome: '',
  obrigatorio: true,
  tiposAceitos: 'application/pdf',
});

/**
 * Fase 4 — Configuração dos campos de documentação do formulário público do edital.
 * Padrão por unidade (tenant). Ajuste pontual por edital fica no painel do processo.
 */
export default function PortalEditaisDocsConfigPanel({ setSnackbar }) {
  const { unidadeSelecionada } = useAuth();
  const [campos, setCampos] = useState([CAMPO_VAZIO()]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState('');

  const u = (path) => withUnidade(path, unidadeSelecionada);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      const res = await apiFetch(u('/api/admin/portal-prestador/editais-docs'));
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Falha ao carregar configuração');
      const list = Array.isArray(data.campos) ? data.campos : [];
      setCampos(list.length ? list : [CAMPO_VAZIO()]);
    } catch (e) {
      setErro(e.message || 'Erro ao carregar');
    } finally {
      setLoading(false);
    }
  }, [unidadeSelecionada?.codigo]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const salvar = async () => {
    const limpos = campos
      .map((c) => ({
        key: c.key || `doc_${Date.now()}`,
        nome: String(c.nome || '').trim(),
        obrigatorio: c.obrigatorio !== false,
        tiposAceitos: c.tiposAceitos || 'application/pdf',
      }))
      .filter((c) => c.nome);
    if (!limpos.length) {
      setErro('Informe ao menos um campo de documentação com nome.');
      return;
    }
    setSaving(true);
    setErro('');
    try {
      const res = await apiFetch(u('/api/admin/portal-prestador/editais-docs'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ campos: limpos }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao salvar');
      setCampos(limpos);
      setSnackbar?.({
        open: true,
        message: 'Padrão de documentos do portal salvo para a unidade.',
        severity: 'success',
      });
    } catch (e) {
      setErro(e.message || 'Falha ao salvar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
      <Typography variant="h6" fontWeight={800} color="primary" gutterBottom>
        Portal do Prestador → Editais (documentação)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Padrão multiunidade aplicado a novos editais publicados. O prestador acessa o link{' '}
        <strong>sem cadastro</strong>; estes campos montam o formulário de upload. Em cada edital
        específico ainda é possível ajustar pontualmente.
      </Typography>

      {erro ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro('')}>
          {erro}
        </Alert>
      ) : null}

      {loading ? (
        <Typography variant="body2">Carregando…</Typography>
      ) : (
        <Stack spacing={1.5}>
          {campos.map((c, idx) => (
            <Stack
              key={c.key || idx}
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ sm: 'center' }}
            >
              <TextField
                label="Nome do documento"
                size="small"
                fullWidth
                value={c.nome || ''}
                onChange={(e) =>
                  setCampos((prev) =>
                    prev.map((x, i) => (i === idx ? { ...x, nome: e.target.value } : x))
                  )
                }
              />
              <TextField
                label="Tipos aceitos"
                size="small"
                sx={{ minWidth: 160 }}
                value={c.tiposAceitos || 'application/pdf'}
                onChange={(e) =>
                  setCampos((prev) =>
                    prev.map((x, i) => (i === idx ? { ...x, tiposAceitos: e.target.value } : x))
                  )
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={c.obrigatorio !== false}
                    onChange={(e) =>
                      setCampos((prev) =>
                        prev.map((x, i) =>
                          i === idx ? { ...x, obrigatorio: e.target.checked } : x
                        )
                      )
                    }
                  />
                }
                label="Obrigatório"
              />
              <IconButton
                color="error"
                onClick={() => setCampos((prev) => prev.filter((_, i) => i !== idx))}
                disabled={campos.length <= 1}
                aria-label="Remover campo"
              >
                <RemoveIcon />
              </IconButton>
            </Stack>
          ))}

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              startIcon={<AddIcon />}
              onClick={() => setCampos((prev) => [...prev, CAMPO_VAZIO()])}
              sx={{ textTransform: 'none' }}
            >
              Adicionar campo
            </Button>
            <Button
              variant="contained"
              onClick={salvar}
              disabled={saving}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              {saving ? 'Salvando…' : 'Salvar padrão'}
            </Button>
          </Stack>

          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" fontWeight={800}>
            Pré-visualização (prestador)
          </Typography>
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: 'rgba(15,23,42,0.03)',
              border: '1px solid rgba(15,23,42,0.08)',
            }}
          >
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Campos fixos: Empresa · CNPJ · E-mail · Telefone · Valor
            </Typography>
            {campos
              .filter((c) => String(c.nome || '').trim())
              .map((c) => (
                <Typography key={c.key} variant="body2" sx={{ mb: 0.5 }}>
                  • {c.nome}
                  {c.obrigatorio !== false ? ' *' : ' (opcional)'} — PDF
                </Typography>
              ))}
          </Box>
        </Stack>
      )}
    </Paper>
  );
}
